/**
 * Vitec Express Connect credentials — shared by the web app (which decides
 * whether to offer the integration at all) and the runtime (which makes the
 * calls).
 *
 * NOT OAuth, and this is the fact everything else follows from. Express
 * Connect authenticates with HTTP Basic using PARTNER-level credentials:
 * Squidler registers once with Vitec, and each realtor office then sends Vitec
 * a written consent ("medgivande") that activates our partner account for
 * their customer id. There is no consent screen, no token, no refresh and no
 * expiry — see docs/vitec-maklarsystem-integration.md §2.3 and §2.8.
 *
 * Vitec offers the API keys EITHER as one set shared across all customers OR
 * minted per customer ("kan dessa antingen delas mellan samtliga kunder eller
 * skapas separat per kund … vi rekommenderar en gemensam uppsättning",
 * Express Connect's technical product owner, 2026-09-17). We ask for the
 * shared set, so the env holds the normal credential:
 *
 *   VITEC_CONNECT_USERNAME   the partner username (required to offer the integration)
 *   VITEC_CONNECT_PASSWORD   the shared password (expected; a deployment with
 *                            the username alone runs in per-customer mode)
 *   VITEC_CONNECT_BASE_URL   optional; defaults to production
 *
 * Per-customer keys remain possible — the connection's sealed document holds them, and a
 * row there wins over the env for its office — but they are the exception.
 * The resolution order (per-category row → '*' row → env password) lives in
 * the runtime's `tools/vitec/connections.ts`; this module only knows the env.
 *
 * Vitec's own guidance (Bilaga 2 §3.1 f, §16): the password must be stored
 * encrypted, never hard-coded, and any unauthorised disclosure reported to
 * Vitec immediately. A `401` from the API therefore means OUR partner
 * credential is wrong and is an ops alert, never a per-customer state.
 */

export const VITEC_DEFAULT_BASE_URL = "https://connect.maklare.vitec.net";

export interface VitecCredential {
  readonly username: string;
  readonly password: string;
}

interface VitecConfig {
  readonly username: string;
  /** The shared password; null only in per-customer-key mode. */
  readonly password: string | null;
  readonly baseUrl: string;
}

/** The API root — production unless the env points at the demo environment. */
export const vitecBaseUrl = (): string =>
  (process.env.VITEC_CONNECT_BASE_URL || VITEC_DEFAULT_BASE_URL).replace(/\/+$/, "");

/** Read the env, or null when no partner username is set. Both the getter and
 *  the configured-check go through here so the var names appear once. */
const readConfig = (): VitecConfig | null => {
  const username = process.env.VITEC_CONNECT_USERNAME;
  if (!username) return null;
  const password = process.env.VITEC_CONNECT_PASSWORD || null;
  return { username, password, baseUrl: vitecBaseUrl() };
};

/**
 * Whether this deployment can talk to Vitec at all. Read at request time,
 * never baked in: the same artifact is promoted dev → prod, and this is how an
 * integration ships dark on a box without credentials.
 */
export const isVitecConfigured = (): boolean => readConfig() !== null;

export const getVitecConfig = (): VitecConfig => {
  const config = readConfig();
  if (!config) {
    throw new Error(
      "Vitec Express Connect not configured — set VITEC_CONNECT_USERNAME (the partner username)",
    );
  }
  return config;
};

/** The `Authorization` header value for a credential pair. */
export const basicAuthHeader = ({ username, password }: VitecCredential): string =>
  `Basic ${Buffer.from(`${username}:${password}`).toString("base64")}`;
